import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../components/ui/Icon';
import ToolCard from '../components/tools/ToolCard';
import { FadeInWhenVisible, StaggerContainer, StaggerItem } from '../components/ui/Motion';
import { TOOLS, TOOL_CATEGORIES, searchTools, getToolsByCategory } from '../data/tools';

const ToolsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const activeCategory = searchParams.get('cat') || null;

  const filteredTools = useMemo(() => {
    let tools = query ? searchTools(query) : TOOLS;
    if (activeCategory) tools = tools.filter(t => t.category === activeCategory);
    return tools;
  }, [query, activeCategory]);

  const categories = Object.values(TOOL_CATEGORIES);

  return (
    <div className="min-h-screen pt-24 pb-16" style={{ background: 'var(--bg-primary)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            All <span className="gradient-text">Document Tools</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-lg max-w-xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            40+ professional tools to convert, edit, merge, and secure your documents.
          </motion.p>
        </div>

        {/* Search & Controls */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="mb-8">
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Icon name="Search" size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
              <input
                type="text"
                placeholder="Search tools (e.g., 'PDF to Word', 'compress')..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="input-field !pl-11"
              />
              {query && (
                <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Icon name="X" size={16} style={{ color: 'var(--text-muted)' }} />
                </button>
              )}
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-1 p-1 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
              {['grid', 'list'].map(mode => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  className={`p-2 rounded-md transition-all ${viewMode === mode ? 'shadow-sm' : ''}`}
                  style={{
                    background: viewMode === mode ? 'var(--bg-primary)' : 'transparent',
                    color: viewMode === mode ? 'var(--text-primary)' : 'var(--text-muted)',
                  }}>
                  <Icon name={mode === 'grid' ? 'Grid' : 'List'} size={16} />
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Category Filter Chips */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="flex flex-wrap gap-2 mb-10">
          <button
            onClick={() => setSearchParams({})}
            className={`tool-chip ${!activeCategory ? '!border-brand-500 !bg-brand-50' : ''}`}
            style={!activeCategory ? { color: '#3374ff' } : {}}>
            All Tools
          </button>
          {categories.map(cat => (
            <button key={cat.id}
              onClick={() => setSearchParams(activeCategory === cat.id ? {} : { cat: cat.id })}
              className="tool-chip flex items-center gap-1.5"
              style={activeCategory === cat.id ? { borderColor: cat.color, background: cat.bgLight, color: cat.color } : {}}>
              <span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
              {cat.label}
              <span className="text-xs opacity-60">({getToolsByCategory(cat.id).length})</span>
            </button>
          ))}
        </motion.div>

        {/* Results Count */}
        <div className="mb-6 text-sm" style={{ color: 'var(--text-muted)' }}>
          {filteredTools.length} tool{filteredTools.length !== 1 ? 's' : ''} found
          {query && <span> for "<strong>{query}</strong>"</span>}
        </div>

        {/* Tools Grid / List */}
        <AnimatePresence mode="wait">
          {filteredTools.length === 0 ? (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="text-center py-20">
              <Icon name="Search" size={48} className="mx-auto mb-4 opacity-20" />
              <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>No tools found</h3>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Try adjusting your search or filter criteria.</p>
            </motion.div>
          ) : (
            <motion.div key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {activeCategory ? (
                // Single category view
                <div className={viewMode === 'grid'
                  ? 'grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                  : 'space-y-2'}>
                  {filteredTools.map((tool, i) => (
                    <motion.div key={tool.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}>
                      <ToolCard tool={tool} variant={viewMode === 'list' ? 'compact' : 'default'} />
                    </motion.div>
                  ))}
                </div>
              ) : (
                // Grouped by category
                <div className="space-y-12">
                  {categories.map(cat => {
                    const catTools = filteredTools.filter(t => t.category === cat.id);
                    if (catTools.length === 0) return null;
                    return (
                      <FadeInWhenVisible key={cat.id}>
                        <div className="flex items-center gap-3 mb-5">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ background: cat.bgLight, color: cat.color }}>
                            <Icon name={cat.icon} size={16} />
                          </div>
                          <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{cat.label}</h2>
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: cat.bgLight, color: cat.color }}>
                            {catTools.length}
                          </span>
                        </div>
                        <div className={viewMode === 'grid'
                          ? 'grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                          : 'space-y-2'}>
                          {catTools.map((tool, i) => (
                            <motion.div key={tool.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.04 }}>
                              <ToolCard tool={tool} variant={viewMode === 'list' ? 'compact' : 'default'} />
                            </motion.div>
                          ))}
                        </div>
                      </FadeInWhenVisible>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ToolsPage;
